// FILE: ProductList/PersonalizedPDFExportModal.jsx
// Modal for configuring and generating a personalized product catalog PDF
// Supports optional customer details, layout selection, and export options

import React, { useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiX,
  FiDownload,
  FiUser,
  FiGrid,
  FiList,
  FiMinus,
  FiCheck,
  FiLoader,
  FiDollarSign,
  FiPackage,
  FiFileText,
  FiPhone,
  FiMail,
  FiChevronDown,
  FiChevronUp,
  FiEye,
} from "react-icons/fi";
import { generateProductCatalogPDF } from "./generateProductCatalogPDF";
import toast from "react-hot-toast";

const PersonalizedPDFExportModal = ({
  isOpen,
  onClose,
  products = [],
  selectedProductIds = [],
}) => {
  // Redux state
  const { user } = useSelector((state) => state.auth);
  const { selectedBranch: branchData } = useSelector((state) => state.branch);
  const branchDisplay = useSelector((state) => state.branchDisplay);

  // Form state
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    email: "",
    note: "",
  });

  const [exportConfig, setExportConfig] = useState({
    showPricing: true,
    showStock: false,
    showBreakdown: false,
    layout: "catalog",
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showCustomerFields, setShowCustomerFields] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [exportScope, setExportScope] = useState(
    selectedProductIds.length > 0 ? "selected" : "all",
  );

  // Ref to prevent stale closure in progress callback
  const progressRef = useRef(null);
  progressRef.current = setProgress;

  // Determine which products to export
  const productsToExport = useMemo(() => {
    if (exportScope === "selected" && selectedProductIds.length > 0) {
      return products.filter((p) => selectedProductIds.includes(p._id));
    }
    return products;
  }, [products, selectedProductIds, exportScope]);

  // Branch display info for preview
  const displayInfo = useMemo(() => {
    const display = branchDisplay?.display || {};
    return {
      storeName: display?.displayName || branchData?.name || "Store",
      tagline: display?.tagline || "",
      logoUrl: display?.logo?.url || "",
      primaryColor: display?.brandColors?.primary || "#D4AF37",
      hasContact: !!(
        branchDisplay?.contactInfo?.phone?.primary ||
        branchDisplay?.contactInfo?.email?.primary
      ),
    };
  }, [branchDisplay, branchData]);

  // Stable progress callback that won't cause re-render storms
  const handleProgress = useCallback((percent) => {
    progressRef.current(percent);
  }, []);

  const handleGenerate = async () => {
    if (productsToExport.length === 0) {
      toast.error("No products to export");
      return;
    }

    setIsGenerating(true);
    setProgress(0);

    // CRITICAL: Wait for 2 animation frames so the browser fully paints
    // the loading state before we start heavy synchronous PDF work
    await new Promise((resolve) =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setTimeout(resolve, 100)),
      ),
    );

    try {
      const customerData = customerInfo.name.trim()
        ? {
            name: customerInfo.name.trim(),
            phone: customerInfo.phone.trim() || undefined,
            email: customerInfo.email.trim() || undefined,
            note: customerInfo.note.trim() || undefined,
          }
        : null;

      // Generate the blob directly (with progress callback)
      const blob = await generateProductCatalogPDF({
        products: productsToExport,
        branchDisplay,
        branchData: branchData || {},
        customerInfo: customerData,
        exportConfig,
        user,
        onProgress: handleProgress,
      });

      // Build filename
      const storeName =
        branchDisplay?.display?.displayName || branchData?.name || "Catalog";
      const dateStr = new Date().toISOString().split("T")[0];
      const fileName = customerData?.name
        ? `${storeName}_Catalog_${customerData.name.replace(/\s+/g, "_")}_${dateStr}.pdf`
        : `${storeName}_Product_Catalog_${dateStr}.pdf`;

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`PDF exported: ${fileName}`);
      onClose();
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-6 py-4 border-b flex items-center justify-between"
            style={{
              background: `linear-gradient(135deg, ${displayInfo.primaryColor}15, ${displayInfo.primaryColor}05)`,
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${displayInfo.primaryColor}20` }}
              >
                <FiFileText
                  size={20}
                  style={{ color: displayInfo.primaryColor }}
                />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Export Product Catalog
                </h2>
                <p className="text-xs text-gray-500">
                  {productsToExport.length} product
                  {productsToExport.length !== 1 ? "s" : ""} •{" "}
                  {displayInfo.storeName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Generating overlay inside body */}
            {isGenerating && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <FiLoader className="animate-spin text-primary" size={20} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Generating PDF...
                    </p>
                    <p className="text-xs text-gray-500">
                      {progress < 30
                        ? "Loading images..."
                        : progress < 90
                          ? "Rendering products..."
                          : "Finalizing..."}
                    </p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300 ease-out"
                    style={{
                      width: `${Math.max(progress, 2)}%`,
                      backgroundColor: displayInfo.primaryColor,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1.5 text-right">
                  {progress}%
                </p>
              </div>
            )}

            {/* Export Scope */}
            {selectedProductIds.length > 0 && !isGenerating && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Products to Export
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setExportScope("selected")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      exportScope === "selected"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <FiCheck
                      className={`inline mr-1.5 ${exportScope === "selected" ? "" : "opacity-0"}`}
                      size={14}
                    />
                    Selected ({selectedProductIds.length})
                  </button>
                  <button
                    onClick={() => setExportScope("all")}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      exportScope === "all"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <FiCheck
                      className={`inline mr-1.5 ${exportScope === "all" ? "" : "opacity-0"}`}
                      size={14}
                    />
                    All Products ({products.length})
                  </button>
                </div>
              </div>
            )}

            {/* Customer Personalization Toggle */}
            {!isGenerating && (
              <div>
                <button
                  onClick={() => setShowCustomerFields(!showCustomerFields)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FiUser className="text-gray-500" size={16} />
                    <span className="text-sm font-medium text-gray-700">
                      Personalize for Customer
                    </span>
                    {customerInfo.name && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {customerInfo.name}
                      </span>
                    )}
                  </div>
                  {showCustomerFields ? (
                    <FiChevronUp className="text-gray-400" size={16} />
                  ) : (
                    <FiChevronDown className="text-gray-400" size={16} />
                  )}
                </button>

                {showCustomerFields && (
                  <div className="mt-3 space-y-3 pl-2">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">
                        Customer Name{" "}
                        <span className="text-gray-400">(optional)</span>
                      </label>
                      <div className="relative">
                        <FiUser
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={14}
                        />
                        <input
                          type="text"
                          placeholder="e.g. Mrs. Sharma"
                          value={customerInfo.name}
                          onChange={(e) =>
                            setCustomerInfo((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                          Phone
                        </label>
                        <div className="relative">
                          <FiPhone
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={12}
                          />
                          <input
                            type="tel"
                            placeholder="+91 98765..."
                            value={customerInfo.phone}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                phone: e.target.value,
                              }))
                            }
                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                          Email
                        </label>
                        <div className="relative">
                          <FiMail
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={12}
                          />
                          <input
                            type="email"
                            placeholder="customer@email.com"
                            value={customerInfo.email}
                            onChange={(e) =>
                              setCustomerInfo((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">
                        Personal Note
                      </label>
                      <textarea
                        placeholder="e.g. Thank you for visiting our store! These are the pieces you liked..."
                        value={customerInfo.note}
                        onChange={(e) =>
                          setCustomerInfo((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Layout Selection */}
            {!isGenerating && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Layout Style
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      value: "catalog",
                      label: "Catalog",
                      icon: FiGrid,
                      desc: "Rich cards with images",
                    },
                    {
                      value: "table",
                      label: "Table",
                      icon: FiList,
                      desc: "Structured rows",
                    },
                    {
                      value: "compact",
                      label: "Compact",
                      icon: FiMinus,
                      desc: "Simple list",
                    },
                  ].map((option) => {
                    const Icon = option.icon;
                    const isSelected = exportConfig.layout === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() =>
                          setExportConfig((prev) => ({
                            ...prev,
                            layout: option.value,
                          }))
                        }
                        className={`p-3 rounded-lg border text-center transition-all ${
                          isSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <Icon
                          size={20}
                          className={`mx-auto mb-1 ${isSelected ? "text-primary" : "text-gray-400"}`}
                        />
                        <span
                          className={`text-sm font-medium block ${isSelected ? "text-primary" : "text-gray-700"}`}
                        >
                          {option.label}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {option.desc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Display Options */}
            {!isGenerating && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Include in Export
                </label>
                <div className="space-y-2">
                  {[
                    {
                      key: "showPricing",
                      label: "Show Pricing",
                      icon: FiDollarSign,
                      desc: "Display product prices",
                    },
                    {
                      key: "showBreakdown",
                      label: "Price Breakdown",
                      icon: FiEye,
                      desc: "Metal value, making charges, GST",
                      depends: "showPricing",
                    },
                    {
                      key: "showStock",
                      label: "Stock Availability",
                      icon: FiPackage,
                      desc: "Show available quantity",
                    },
                  ].map((option) => {
                    const Icon = option.icon;
                    const isDisabled =
                      option.depends && !exportConfig[option.depends];
                    const isChecked = exportConfig[option.key];

                    return (
                      <label
                        key={option.key}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                          isDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gray-50"
                        } ${isChecked && !isDisabled ? "bg-gray-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) =>
                            setExportConfig((prev) => ({
                              ...prev,
                              [option.key]: e.target.checked,
                              ...(option.key === "showPricing" &&
                                !e.target.checked && { showBreakdown: false }),
                            }))
                          }
                          className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                        />
                        <Icon
                          size={16}
                          className="text-gray-400 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-700">
                            {option.label}
                          </span>
                          <span className="text-xs text-gray-400 ml-2">
                            {option.desc}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Branch Info Preview */}
            {!isGenerating && (
              <div>
                <button
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  {showAdvancedOptions ? (
                    <FiChevronUp size={12} />
                  ) : (
                    <FiChevronDown size={12} />
                  )}
                  Branch details included in PDF
                </button>

                {showAdvancedOptions && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                    <p>
                      <strong>Store:</strong> {displayInfo.storeName}
                      {displayInfo.tagline && ` — ${displayInfo.tagline}`}
                    </p>
                    {displayInfo.logoUrl && (
                      <p className="flex items-center gap-1">
                        <FiCheck size={10} className="text-green-500" />
                        Logo will be included
                      </p>
                    )}
                    {displayInfo.hasContact && (
                      <p className="flex items-center gap-1">
                        <FiCheck size={10} className="text-green-500" />
                        Contact info & address included
                      </p>
                    )}
                    <p className="flex items-center gap-1">
                      <span
                        className="w-3 h-3 rounded-full inline-block border"
                        style={{ backgroundColor: displayInfo.primaryColor }}
                      />
                      Brand colors applied to PDF
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating || productsToExport.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isGenerating ? (
                <>
                  <FiLoader className="animate-spin" size={16} />
                  Generating... {progress > 0 ? `${progress}%` : ""}
                </>
              ) : (
                <>
                  <FiDownload size={16} />
                  Export PDF ({productsToExport.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalizedPDFExportModal;
